import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Card,
    CardContent,
    Button,
    Typography,
    TextField,
    Grid,
    Container,
    CardMedia,
    Box,
    InputAdornment,
    Snackbar,
} from '@material-ui/core';
import { Alert } from "@material-ui/lab";
import SearchIcon from '@material-ui/icons/Search';
import '../assets/styles/SmartCMAPage.css';
import { useCMAs } from "./CMAContext";
import Breadcrumb from './BreadcrumbsComponent';
import api, { consoleDevLog } from "../apiConfig";
import GenerateModal from './Step4/GenerateModal'; 
// ^ Adjust import path as needed

const SmartCMAPage = () => {
    const location = useLocation();
    const { cmas } = useCMAs();

    const [searchTerm, setSearchTerm] = useState('');
    const [combinedCMAs, setCombinedCMAs] = useState([]);
    const [showSuccessBanner, setShowSuccessBanner] = useState(false);

    // For opening the GenerateModal
    const [openModal, setOpenModal] = useState(false);
    const [selectedCma, setSelectedCma] = useState(null);

    useEffect(() => {
        // Show success banner if redirected with a state indicating successful CMA creation
        if (location.state && location.state.cmaCreated) {
            setShowSuccessBanner(true);
        }

        api.get('/cma')
            .then(res => {
                consoleDevLog("CMAs from API:", res.data);
                setCombinedCMAs(res.data);
            })
            .catch(err => console.log(err));
    }, [location.state]);

    // Filtered list based on search
    const filteredCMAs = combinedCMAs.filter(cma => {
        const s = searchTerm.toLowerCase();
        const cmaNameMatch =
            cma.cmaName && cma.cmaName.toLowerCase().includes(s);
        const addressMatch =
            cma.subjectProperty?.UnparsedAddress &&
            cma.subjectProperty.UnparsedAddress.toLowerCase().includes(s);
        const clientMatch =
            cma.client && cma.client.toLowerCase().includes(s);
        return cmaNameMatch || addressMatch || clientMatch;
    });

    return (
        <Container>
            <Grid container spacing={3}>
                <Breadcrumb />

                <Grid item xs={12} className="flex-container">
                    <Typography variant="h4" className="header-title">
                        Recently Created CMAs
                    </Typography>

                    <div style={{ marginBottom: "-40px" }}>
                        <Link to="/smart-cma/new-cma">
                            <Button
                                variant="contained"
                                color="primary"
                                className="create-btn"
                            >
                                Create New CMA
                            </Button>
                        </Link>
                    </div>
                </Grid>

                {/* Success Banner */}
                <Snackbar
                    open={showSuccessBanner}
                    autoHideDuration={6000}
                    onClose={() => setShowSuccessBanner(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setShowSuccessBanner(false)}
                        severity="success"
                        sx={{ width: '100%' }}
                    >
                        CMA created successfully!
                    </Alert>
                </Snackbar>
                <hr />

                <Grid item xs={12}>
                    <Box position="relative" className="cma-search">
                        <TextField
                            variant="standard"
                            placeholder="Search by CMA name, address, or client..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Grid container spacing={3}>
                        {filteredCMAs.map(cma => (
                            <Grid item xs={12} md={6} key={cma.id}>
                                <Card className="cma-card" style={{ display: 'flex', flexDirection: 'column' }}>
                                    {/* Top row: image + text */}
                                    <Grid container spacing={0}>
                                        <Grid item xs={5}>
                                            <CardMedia
                                                component="img"
                                                alt={cma.subjectProperty?.Addr || ''}
                                                height="140"
                                                image={
                                                    // 1) If we have custom or uploaded images, use the first one:
                                                    cma.subjectProperty?.imageUrls?.[0]
                                                    // 2) Otherwise, fall back to MLS bucket (old behavior):
                                                    || (cma.subjectProperty?.Ml_num
                                                    ? `https://realtime-home-images.s3.amazonaws.com/${cma.subjectProperty.Ml_num}/1.jpeg`
                                                    : '')
                                                }
                                                title={cma.subjectProperty?.Addr}
                                                className="cma-image"
                                            />
                                        </Grid>
                                        <Grid item xs={7} className="cma-content">
                                            <Typography variant="h5">{cma.cmaName}</Typography>
                                            <Typography variant="body2">
                                                Address: {cma.subjectProperty?.full_address}
                                            </Typography>
                                            <Typography variant="body2">
                                                Client: {cma.client}
                                            </Typography>
                                            {cma.priceRange && (
                                                <Typography variant="body2">
                                                    Price Range: ${cma.priceRange.lower} - ${cma.priceRange.upper}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>

                                    {/* Bottom row: Edit + Export */}
                                    <Grid container spacing={0} style={{ marginTop: 'auto' }}>
                                        <Grid item xs={5}>
                                            <Link
                                                to={{
                                                    pathname: '/smart-cma/new-cma',
                                                    state: { cmaData: cma },
                                                }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    fullWidth
                                                    style={{ borderRadius: 0 }}
                                                >
                                                    EDIT
                                                </Button>
                                            </Link>
                                        </Grid>
                                        <Grid item xs={7}>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                style={{ borderRadius: 0 }}
                                                onClick={() => {
                                                    // Instead of downloading directly,
                                                    // open the GenerateModal
                                                    setSelectedCma(cma);
                                                    setOpenModal(true);
                                                }}
                                            >
                                                EXPORT
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>

            {/* GenerateModal - only show if openModal is true */}
            {selectedCma && (
                <GenerateModal
                    shouldRedirect={true}
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    // Pass the CMA data so it auto-fills
                    cmaData={selectedCma} 
                    history={{
                      push: (pathObj) => {
                        // Minor mock so your "history.push" calls inside the modal
                        // won't cause an error if you haven't used react-router v5's "withRouter"
                        // You can adapt as needed if you already have a real 'history'
                        window.location.href = pathObj.pathname;
                      }
                    }}
                />
            )}
        </Container>
    );
};

export default SmartCMAPage;